import {
  BaseResource,
  IdResourceParam,
  paginatedListSerializer,
  ResourceAction,
  ResourceActionV2,
} from "@megarax/rest-resource";
import { Serializers } from "@megarax/serializers";

import { NewUserInput, UpdateUserInput } from "./commands";
import {
  UserDto,
  UserListFilters,
  UserProfileDto,
  UserProfileListFilters,
  UserSimple,
} from "./queries";

const list = ResourceAction({
  name: "list",
  method: "get",
  path: "",
  querySerializer: Serializers.object<UserListFilters>({
    searchText: Serializers.string.optional(),
    search: Serializers.string.optional(),
    limit: Serializers.integer.optional(),
    offset: Serializers.integer.optional(),
    includeDeleted: Serializers.boolean.optional(),
  }),
  responseSerializer: paginatedListSerializer(
    Serializers.object<UserSimple>({
      id: Serializers.integer,
      email: Serializers.string,
      isDeleted: Serializers.boolean,
    }),
  ),
});

export type { UserDto, UserListFilters, UserProfileDto, UserSimple, NewUserInput, UpdateUserInput };

export const userResource = BaseResource(["iam", "users"], {
  current: ResourceAction({
    name: "currentUser",
    method: "get",
    path: "current",
    responseSerializer: Serializers.object({
      id: Serializers.integer,
      email: Serializers.string,
    }),
  }),
  create: ResourceAction({
    name: "create",
    method: "post",
    path: "",
    requestBodySerializer: Serializers.object<NewUserInput>({
      email: Serializers.string,
      groups: Serializers.array(
        Serializers.object({
          uuid: Serializers.uuid(),
        }),
      ),
    }),
  }),
  list,
  byId: IdResourceParam("userId", {
    delete: ResourceActionV2({
      name: "delete",
      method: "delete",
      path: "",
      responseErrorSerializer: Serializers.stringOneOf("UserAlreadyDeleted"),
    }),
    restore: ResourceActionV2({
      name: "restore",
      method: "post",
      responseErrorSerializer: Serializers.stringOneOf("UserNotDeleted"),
    }),
    retrieve: ResourceAction({
      name: "retrieve",
      method: "get",
      path: "",
      responseSerializer: Serializers.object<UserDto>({
        email: Serializers.string,
        id: Serializers.integer,
        groups: Serializers.array(
          Serializers.object({
            name: Serializers.string,
            uuid: Serializers.uuid(),
          }),
        ),
        isDeleted: Serializers.boolean,
      }),
    }),
    update: ResourceAction({
      name: "update",
      method: "put",
      path: "",
      requestBodySerializer: Serializers.object<UpdateUserInput>({
        email: Serializers.string,
        groups: Serializers.array(
          Serializers.object({
            uuid: Serializers.uuid(),
          }),
        ),
      }),
    }),
  }),
});

export const userProfileDtoSerializer = Serializers.object<UserProfileDto>({
  user: Serializers.object({
    id: Serializers.integer,
    email: Serializers.string,
  }),
  firstName: Serializers.string.optional(),
  lastName: Serializers.string.optional(),
  pictureUrl: Serializers.optional(Serializers.string.nullable()),
});

const userProfileListAction = ResourceAction({
  name: "all",
  method: "get",
  path: "",
  responseSerializer: paginatedListSerializer(userProfileDtoSerializer),
  querySerializer: Serializers.object<UserProfileListFilters>({
    limit: Serializers.integer.optional(),
    offset: Serializers.integer.optional(),
    searchText: Serializers.string.optional(),
  }),
});

export const profileResource = BaseResource(["iam", "profiles"], {
  byId: IdResourceParam("userId", {
    retrieve: ResourceAction({
      name: "retrieve",
      method: "get",
      path: "",
      responseSerializer: userProfileDtoSerializer,
    }),
  }),
  list: userProfileListAction,
});
